// Tamil-Katral-Maiyam\src\components\marketing\StepTimeline.tsx

import React from "react";
import { LucideIcon } from "lucide-react";
import { useInView } from "../../hooks/useInView";
import { clsx } from "clsx";

export interface StepItem {
  step: number;
  title: string;
  description: string;
  icon: LucideIcon;
}

interface StepTimelineProps {
  steps: StepItem[];
  className?: string;
}

export const StepTimeline: React.FC<StepTimelineProps> = ({
  steps,
  className,
}) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <div ref={ref} className={clsx("relative", className)}>
      {/* Desktop: horizontal timeline */}
      <div className="hidden lg:grid lg:grid-cols-4 lg:gap-6 lg:items-stretch">
        {steps.map((s, i) => (
          <div
            key={s.step}
            className={clsx(
              "relative h-full transition-all duration-700 ease-out",
              inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            )}
            style={{ transitionDelay: inView ? `${i * 100}ms` : "0ms" }}
          >
            {/* connector line */}
            {i < steps.length - 1 && (
              <div
                className={clsx(
                  "absolute top-7 left-[calc(50%+32px)] w-[calc(100%-64px)] h-[3px] -translate-y-1/2 rounded-full",
                  "bg-white/22",
                  inView ? "opacity-100" : "opacity-0"
                )}
                style={{
                  transitionDelay: inView ? `${(i + 1) * 100}ms` : "0ms",
                }}
                aria-hidden
              />
            )}

            {/* ✅ Make full height + align all cards evenly */}
            <div className="flex h-full flex-col items-center text-center">
              {/* icon circle (glassy) */}
              <div
                className={clsx(
                  "flex h-14 w-14 items-center justify-center rounded-2xl",
                  "border border-white/18 bg-white/12 backdrop-blur-md",
                  "shadow-[0_18px_40px_rgba(0,0,0,0.35)]",
                  "text-white drop-shadow-[0_3px_14px_rgba(0,0,0,0.75)]"
                )}
              >
                <s.icon className="h-7 w-7" aria-hidden />
              </div>

              {/* step label */}
              <span
                className={clsx(
                  "mt-3 inline-flex items-center gap-2",
                  "rounded-full border border-white/18 bg-black/25 px-3 py-1",
                  "text-xs font-bold uppercase tracking-wider text-white/95",
                  "drop-shadow-[0_3px_12px_rgba(0,0,0,0.70)]"
                )}
              >
                Step {s.step}
              </span>

              {/* ✅ glass card - fixed equal height */}
              <div
                className={clsx(
                  "mt-3 w-full rounded-2xl px-5 py-4",
                  "border border-white/16 bg-white/10 backdrop-blur-md",
                  "shadow-[0_18px_40px_rgba(0,0,0,0.35)]",
                  // ✅ this makes all boxes same height + same bottom alignment
                  "h-full min-h-[170px] flex flex-col justify-center"
                )}
              >
                <h3 className="text-lg font-extrabold text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.75)]">
                  {s.title}
                </h3>

                <p className="mt-1 text-sm font-semibold text-white/90 leading-relaxed drop-shadow-[0_3px_12px_rgba(0,0,0,0.65)]">
                  {s.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: vertical timeline */}
      <div className="lg:hidden space-y-5">
        {steps.map((s, i) => (
          <div
            key={s.step}
            className={clsx(
              "relative flex gap-4 transition-all duration-700 ease-out",
              inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
            )}
            style={{ transitionDelay: inView ? `${i * 100}ms` : "0ms" }}
          >
            {/* vertical connector */}
            {i < steps.length - 1 && (
              <div
                className="absolute left-[26px] top-[58px] bottom-0 w-px bg-white/22 -mb-6"
                aria-hidden
              />
            )}

            {/* icon */}
            <div
              className={clsx(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",
                "border border-white/18 bg-white/12 backdrop-blur-md",
                "shadow-[0_18px_40px_rgba(0,0,0,0.35)]",
                "text-white drop-shadow-[0_3px_14px_rgba(0,0,0,0.75)]"
              )}
            >
              <s.icon className="h-6 w-6" aria-hidden />
            </div>

            {/* card */}
            <div className="flex-1 pb-6">
              <span
                className={clsx(
                  "inline-flex rounded-full border border-white/18 bg-black/25 px-3 py-1",
                  "text-xs font-bold uppercase tracking-wider text-white/95",
                  "drop-shadow-[0_3px_12px_rgba(0,0,0,0.70)]"
                )}
              >
                Step {s.step}
              </span>

              <div
                className={clsx(
                  "mt-3 rounded-2xl px-5 py-4",
                  "border border-white/16 bg-white/10 backdrop-blur-md",
                  "shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
                )}
              >
                <h3 className="text-lg font-extrabold text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.75)]">
                  {s.title}
                </h3>
                <p className="mt-1 text-sm font-semibold text-white/90 leading-relaxed drop-shadow-[0_3px_12px_rgba(0,0,0,0.65)]">
                  {s.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
